import * as React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type PaginationState,
  type RowSelectionState,
  type Column,
} from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table';
import { Button } from './button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import { cn } from '@/lib/utils';

export interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  /** Global filter string (e.g. for search-as-you-type). */
  globalFilter?: string;
  /** Click handler for a row (e.g. open detail). */
  onRowClick?: (row: TData) => void;
  /** Slot rendered when filtered data is empty. */
  emptyState?: React.ReactNode;
  /** Initial page size; defaults to 20. */
  pageSize?: number;
  /** Initial sort state, e.g. [{ id: 'name', desc: false }]. */
  initialSorting?: SortingState;
  /** Initial pagination state. Defaults to `{ pageIndex: 0, pageSize }`. */
  initialPagination?: PaginationState;
  /**
   * Fires whenever the sort state changes (header click, programmatic set).
   * Use this to sync sort to URL params or external state. The callback runs
   * AFTER the internal state has updated.
   */
  onSortingChange?: (sorting: SortingState) => void;
  /**
   * Fires whenever pagination state changes (page change, page-size change).
   * Use this to sync pagination to URL params or external state.
   */
  onPaginationChange?: (pagination: PaginationState) => void;
  /** Optional className on the outer wrapper. */
  className?: string;
  /**
   * Stable row identifier extractor. If provided, each rendered `<tr>` gets
   * `data-testid="row-<id>"` (useful for tests) and TanStack uses the id for
   * row identity across data updates.
   */
  getRowId?: (row: TData) => string;
  /**
   * Per-row HTML attribute overrides (data-state, style, etc.). Spread onto
   * each rendered `<tr>` AFTER the default `data-state` + `data-testid` +
   * `onClick` are set, so caller-supplied values win.
   */
  rowProps?: (row: TData) => React.HTMLAttributes<HTMLTableRowElement>;
  /**
   * Controlled row-selection state (TanStack `RowSelectionState`, i.e.
   * `Record<rowId, boolean>` where rowId is whatever `getRowId` returns).
   * When provided together with `onRowSelectionChange`, the table runs in
   * multi-row-selection mode. Caller is responsible for adding a checkbox
   * column to `columns` that reads `row.getIsSelected()` / writes via
   * `row.toggleSelected()`.
   */
  rowSelection?: RowSelectionState;
  /** Callback fired when row selection changes. */
  onRowSelectionChange?: (state: RowSelectionState) => void;
  /** Disable pagination entirely (show all rows). */
  hidePagination?: boolean;
  /**
   * Server-driven pagination (#964). When set, the component does not slice
   * `data` — the caller supplies exactly one page — and `pageCount`/`totalRows`
   * drive the footer. Omit for the default client-side behavior.
   */
  manualPagination?: boolean;
  /**
   * Server-driven sorting (#964). When set, `data` is rendered in the order it
   * was given; use `onSortingChange` to refetch.
   */
  manualSorting?: boolean;
  /** Total pages reported by the server. Pass alongside `manualPagination`. */
  pageCount?: number;
  /** Total rows reported by the server, shown in the footer. */
  totalRows?: number;
  /** Controlled sort state. When provided it overrides the internal state. */
  sorting?: SortingState;
  /** Controlled pagination state. When provided it overrides the internal state. */
  pagination?: PaginationState;
  /**
   * Reset to the first page whenever `data` changes (TanStack default for
   * client-side pagination). Pass `false` when `data` is refetched in the
   * background, so a poll does not throw the user back to page 1 (#2161).
   */
  autoResetPageIndex?: boolean;
}

export function DataTable<TData>({
  data,
  columns,
  globalFilter,
  onRowClick,
  emptyState,
  pageSize = 20,
  initialSorting = [],
  initialPagination,
  onSortingChange,
  onPaginationChange,
  className,
  getRowId,
  rowProps,
  rowSelection,
  onRowSelectionChange,
  hidePagination = false,
  manualPagination = false,
  manualSorting = false,
  pageCount,
  totalRows,
  sorting: controlledSorting,
  pagination: controlledPagination,
  autoResetPageIndex,
}: DataTableProps<TData>) {
  const [internalSorting, setSortingState] = React.useState<SortingState>(initialSorting);
  const [internalPagination, setPaginationState] = React.useState<PaginationState>(
    initialPagination ?? { pageIndex: 0, pageSize }
  );

  // Controlled state wins when supplied (server mode, #964); otherwise the
  // component owns it exactly as before.
  const sorting = controlledSorting ?? internalSorting;
  const pagination = controlledPagination ?? internalPagination;

  const handleSortingChange = React.useCallback<typeof setSortingState>(
    (updater) => {
      setSortingState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        onSortingChange?.(next);
        return next;
      });
    },
    [onSortingChange]
  );

  const handlePaginationChange = React.useCallback<typeof setPaginationState>(
    (updater) => {
      // Controlled: step from the value the caller passed, not the internal
      // copy, which goes stale when the caller changes the page itself
      // (e.g. a filter change resets the URL page, #2161).
      if (controlledPagination) {
        const next =
          typeof updater === 'function' ? updater(controlledPagination) : updater;
        setPaginationState(next);
        onPaginationChange?.(next);
        return;
      }
      setPaginationState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        onPaginationChange?.(next);
        return next;
      });
    },
    [controlledPagination, onPaginationChange]
  );

  const handleInternalRowSelectionChange = React.useCallback(
    (updater: React.SetStateAction<RowSelectionState>) => {
      if (!onRowSelectionChange) return;
      const prev = rowSelection ?? {};
      const next = typeof updater === 'function' ? (updater as (s: RowSelectionState) => RowSelectionState)(prev) : updater;
      onRowSelectionChange(next);
    },
    [onRowSelectionChange, rowSelection]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
      globalFilter,
      ...(onRowSelectionChange ? { rowSelection: rowSelection ?? {} } : {}),
    },
    onSortingChange: handleSortingChange,
    onPaginationChange: handlePaginationChange,
    manualPagination,
    manualSorting,
    ...(autoResetPageIndex !== undefined ? { autoResetPageIndex } : {}),
    ...(manualPagination && pageCount !== undefined ? { pageCount } : {}),
    ...(onRowSelectionChange
      ? { onRowSelectionChange: handleInternalRowSelectionChange, enableRowSelection: true }
      : {}),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(getRowId ? { getRowId: (row: TData) => getRowId(row) } : {}),
  });

  const rows = table.getRowModel().rows;

  return (
    <div className={cn('space-y-2', className)}>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => {
                  const sorted = h.column.getIsSorted();
                  const ariaSort = h.column.getCanSort()
                    ? sorted === 'asc'
                      ? 'ascending'
                      : sorted === 'desc'
                        ? 'descending'
                        : 'none'
                    : undefined;
                  return (
                    <TableHead key={h.id} aria-sort={ariaSort}>
                      {h.isPlaceholder
                        ? null
                        : flexRender(h.column.columnDef.header, h.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyState ?? 'No results.'}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const extra = rowProps ? rowProps(row.original) : undefined;
                return (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  data-testid={getRowId ? `row-${row.id}` : undefined}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={onRowClick ? 'cursor-pointer' : undefined}
                  {...extra}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {!hidePagination && (
        <DataTablePagination
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          pageCount={table.getPageCount()}
          rowCount={table.getRowModel().rows.length}
          filteredRowCount={totalRows ?? table.getFilteredRowModel().rows.length}
          canPreviousPage={table.getCanPreviousPage()}
          canNextPage={table.getCanNextPage()}
          setPageIndex={table.setPageIndex}
          previousPage={table.previousPage}
          nextPage={table.nextPage}
          setPageSize={table.setPageSize}
        />
      )}
    </div>
  );
}

interface DataTablePaginationProps {
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  rowCount: number;
  filteredRowCount: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  setPageIndex: (index: number) => void;
  previousPage: () => void;
  nextPage: () => void;
  setPageSize: (size: number) => void;
}

function DataTablePagination({
  pageIndex,
  pageSize,
  pageCount,
  rowCount,
  filteredRowCount,
  canPreviousPage,
  canNextPage,
  setPageIndex,
  previousPage,
  nextPage,
  setPageSize,
}: DataTablePaginationProps) {
  return (
    <div className="flex items-center justify-between gap-4 px-2 text-sm text-muted-foreground">
      <div>
        Showing {rowCount} of {filteredRowCount}
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span>Rows per page</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="h-8 w-[70px]" aria-label="Rows per page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          Page {pageIndex + 1} of {Math.max(pageCount, 1)}
        </div>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setPageIndex(0)}
            disabled={!canPreviousPage}
            aria-label="First page"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={previousPage}
            disabled={!canPreviousPage}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={nextPage}
            disabled={!canNextPage}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setPageIndex(pageCount - 1)}
            disabled={!canNextPage}
            aria-label="Last page"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Header cell with a sort-toggle button and chevron indicator. Use inside a
 * `ColumnDef.header` function: `header: ({ column }) => <DataTableSortHeader column={column}>Name</DataTableSortHeader>`.
 */
export function DataTableSortHeader<TData, TValue>({
  column,
  children,
}: {
  column: Column<TData, TValue>;
  children: React.ReactNode;
}) {
  const sorted = column.getIsSorted();
  // Derive an aria-label from text children when possible (e.g. "Name" → "Sort by Name").
  const label =
    typeof children === 'string' ? `Sort by ${children}` : undefined;
  return (
    <button
      type="button"
      onClick={() => column.toggleSorting(sorted === 'asc')}
      aria-label={label}
      className="flex items-center gap-1 font-medium hover:text-foreground"
    >
      {children}
      {sorted === 'asc' ? (
        <ChevronUp className="h-3 w-3" />
      ) : sorted === 'desc' ? (
        <ChevronDown className="h-3 w-3" />
      ) : null}
    </button>
  );
}
